import { Task } from "@rapid/types/flows";

export const enum DataMode {
    IMAGE = "image",
    VIDEO = "video"
}

export type DataModeType = (typeof DataMode)[keyof typeof DataMode];

export const enum ImageDeployment {
    SINGLE = "single",
    MULTIPLE = "multiple",
    EDGE = "edge"
}

export type ImageDeploymentType = (typeof ImageDeployment)[keyof typeof ImageDeployment];

export const enum VideoDeployment {
    FILE = "file",
    EDGE = "edge",
    STREAM = "stream"
}

export type VideoDeploymentType = (typeof VideoDeployment)[keyof typeof VideoDeployment];

// This is data needed to build code snippets
// TODO: @maxschridde1494 Maybe this shouldn't be called model data? It's to deploy a workflow, not a model.
export interface ModelDataType {
    apiKey: string;
    workspaceUrl: string;
    workflowUrl: string;
}

// Some of those types/interfaces also exist in the app/functions/rapid/schemas.ts file.
// TODO: move those to a shared file we can use in both frontend and backend.
export enum RapidTrainStatus {
    PENDING = "pending",
    TRAINING = "training",
    FINISHED = "finished",
    FAILED = "failed"
}

export type RapidModelTrainData = { startedAt?: Date; finishedAt?: Date; status: RapidTrainStatus };

export type RapidModelInfo = { id?: string; train: RapidModelTrainData };

export enum AvailableRapidModelArchitectures {
    RF_DETR = "rf_detr",
    RF_INSTANT = "rf_instant"
}

type RapidModelsSavedInfo = {
    [AvailableRapidModelArchitectures.RF_INSTANT]: RapidModelInfo[];
    [AvailableRapidModelArchitectures.RF_DETR]: RapidModelInfo[];
};

// Typing for records saved in rapid_data collection - that is the collection that links
// workflows, models, etc.
export interface RapidDataType {
    id: string;
    task: NonNullable<Task>;
    workspaceId: string;
    workflowId: string;
    models: RapidModelsSavedInfo;
    lastExecutedAt?: { seconds: number; nanoseconds: number };
}

export interface RapidWorkflowDataType {
    id: string;
    url: string;
    name: string;
    rapidDataId: string;
    config: string;
    owner: string; // same as workspaceId
}

export enum CodeSnippetEnv {
    PYTHON = "python",
    CURL = "curl",
    JAVASCRIPT = "javascript",
    SWIFT = "swift",
    NET = "net"
}

export type CodeSnippetEnvType = (typeof CodeSnippetEnv)[keyof typeof CodeSnippetEnv];

export interface CodeSnippetType {
    code: string;
    env: string;
}

export interface IntegrationCopyType {
    title: string;
    icon: string;
    description: string | React.ReactNode;
    codeSnippets: CodeSnippetType[];
    documentationUrl?: string;
}

export interface IntegrationCopyCMSType {
    [DataMode.IMAGE]: Record<ImageDeploymentType, IntegrationCopyType>;
    [DataMode.VIDEO]: Record<VideoDeploymentType, IntegrationCopyType>;
}

const PYTHON_MD = (modelData: ModelDataType): string => `
\`\`\`python
# import the inference-sdk
from inference_sdk import InferenceHTTPClient

# initialize the client
CLIENT = InferenceHTTPClient(
    api_url="https://serverless.roboflow.com",
    api_key="${modelData?.apiKey}"
)

# infer on a local image
result = CLIENT.infer("YOUR_IMAGE.jpg", model_id="${modelData?.workspaceUrl}/${modelData?.workflowUrl}")

\`\`\`
`;

const CURL_MD = (modelData: ModelDataType): string => `
\`\`\`bash
base64 YOUR_IMAGE.jpg | curl -d @- \\
        "https://serverless.roboflow.com/${modelData?.workspaceUrl}/${modelData?.workflowUrl}?api_key=${modelData?.apiKey}"
\`\`\`
`;

const JAVASCRIPT_MD = (modelData: ModelDataType): string => `
\`\`\`js
const axios = require("axios");
const fs = require("fs");

const image = fs.readFileSync("YOUR_IMAGE.jpg", {
    encoding: "base64"
});

axios({
    method: "POST",
    url: "https://serverless.roboflow.com/${modelData?.workspaceUrl}/${modelData?.workflowUrl}",
    params: {
        api_key: "${modelData?.apiKey}"
    },
    data: image,
    headers: {
        "Content-Type": "application/x-www-form-urlencoded"
    }
})
.then(function(response) {
    console.log(response.data);
})
.catch(function(error) {
    console.log(error.message);
});
\`\`\`
`;

const SWIFT_MD = (modelData: ModelDataType): string => `
**Inferring on a Local Image**

\`\`\`swift
import UIKit

// Load Image and Convert to Base64
let image = UIImage(named: "YOUR_IMAGE.jpg") 
let imageData = image?.jpegData(compressionQuality: 1)
let fileContent = imageData?.base64EncodedString()
let postData = fileContent!.data(using: .utf8)

// Initialize Inference Server Request with API_KEY, Model, and Model Version
var request = URLRequest(url: URL(string: "https://serverless.roboflow.com/${modelData?.workspaceUrl}/${modelData?.workflowUrl}?api_key=${modelData?.apiKey}&name=YOUR_IMAGE.jpg")!,timeoutInterval: Double.infinity)
request.addValue("application/x-www-form-urlencoded", forHTTPHeaderField: "Content-Type")
request.httpMethod = "POST"
request.httpBody = postData

// Execute Post Request
URLSession.shared.dataTask(with: request, completionHandler: { data, response, error in
    
    // Parse Response to String
    guard let data = data else {
        print(String(describing: error))
        return
    }
    
    // Convert Response String to Dictionary
    do {
        let dict = try JSONSerialization.jsonObject(with: data, options: []) as? [String: Any]
    } catch {
        print(error.localizedDescription)
    }
    
    // Print String Response
    print(String(data: data, encoding: .utf8)!)
}).resume()
\`\`\`
`;

const NET_MD = (modelData: ModelDataType): string => `
**Uploading a Local Image**

\`\`\`java
using System;
using System.IO;
using System.Net;
using System.Text;

namespace UploadLocal
{
    class UploadLocal
    {

        static void Main(string[] args)
        {
            byte[] imageArray = System.IO.File.ReadAllBytes(@"YOUR_IMAGE.jpg");
            string encoded = Convert.ToBase64String(imageArray);
            byte[] data = Encoding.ASCII.GetBytes(encoded);
            string API_KEY = "${modelData?.apiKey}"
            string DATASET_NAME = "${modelData?.workspaceUrl}"

            // Construct the URL
            string uploadURL =
                    "https://api.roboflow.com/dataset/" +
                            DATASET_NAME + "/upload" +
                            "?api_key=" + API_KEY +
                            "&name=YOUR_IMAGE.jpg" +
                            "&split=train";

            // Service Request Config
            ServicePointManager.Expect100Continue = true;
            ServicePointManager.SecurityProtocol = SecurityProtocolType.Tls12;

            // Configure Request
            WebRequest request = WebRequest.Create(uploadURL);
            request.Method = "POST";
            request.ContentType = "application/x-www-form-urlencoded";
            request.ContentLength = data.Length;

            // Write Data
            using (Stream stream = request.GetRequestStream())
            {
                stream.Write(data, 0, data.Length);
            }

            // Get Response
            string responseContent = null;
            using (WebResponse response = request.GetResponse())
            {
                using (Stream stream = response.GetResponseStream())
                {
                    using (StreamReader sr99 = new StreamReader(stream))
                    {
                        responseContent = sr99.ReadToEnd();
                    }
                }
            }

            Console.WriteLine(responseContent);

        }
    }
}
\`\`\`

**Inferring a Local Image**

\`\`\`java
using System;
using System.IO;
using System.Net;
using System.Text;

namespace InferenceLocal
{
    class InferenceLocal
    {

        static void Main(string[] args)
        {
            byte[] imageArray = System.IO.File.ReadAllBytes(@"YOUR_IMAGE.jpg");
            string encoded = Convert.ToBase64String(imageArray);
            byte[] data = Encoding.ASCII.GetBytes(encoded);
            string API_KEY = "${modelData?.apiKey}"
            string DATASET_NAME = "${modelData?.workspaceUrl}"
            string DATASET_VERSION = "${modelData?.workflowUrl}"

            // Construct the URL
            string uploadURL =
                    "https://serverless.roboflow.com/" + DATASET_NAME + "/" + DATASET_VERSION + "?api_key=" + API_KEY
                + "&name=YOUR_IMAGE.jpg";

            // Service Request Config
            ServicePointManager.Expect100Continue = true;
            ServicePointManager.SecurityProtocol = SecurityProtocolType.Tls12;

            // Configure Request
            WebRequest request = WebRequest.Create(uploadURL);
            request.Method = "POST";
            request.ContentType = "application/x-www-form-urlencoded";
            request.ContentLength = data.Length;

            // Write Data
            using (Stream stream = request.GetRequestStream())
            {
                stream.Write(data, 0, data.Length);
            }

            // Get Response
            string responseContent = null;
            using (WebResponse response = request.GetResponse())
            {
                using (Stream stream = response.GetResponseStream())
                {
                    using (StreamReader sr99 = new StreamReader(stream))
                    {
                        responseContent = sr99.ReadToEnd();
                    }
                }
            }

            Console.WriteLine(responseContent);

        }
    }
}
\`\`\`

**Uploading an Image Hosted Elsewhere via <URL></URL>**

\`\`\`java
using System;
using System.IO;
using System.Net;
using System.Web;

namespace InferenceHosted
{
    class InferenceHosted
    {
        static void Main(string[] args)
        {
            string imageURL = "https://i.ibb.co/jzr27x0/YOUR-IMAGE.jpg";
            string API_KEY = "${modelData?.apiKey}"
            string DATASET_NAME = "${modelData?.workspaceUrl}"
            string DATASET_VERSION = "${modelData?.workflowUrl}"

            // Construct the URL
            string uploadURL =
                    "https://serverless.roboflow.com/" 
                    + DATASET_NAME + "/" + DATASET_VERSION
                    + "?api_key=" + API_KEY
                    + "&image=" + HttpUtility.UrlEncode(imageURL);

            // Service Point Config
            ServicePointManager.Expect100Continue = true;
            ServicePointManager.SecurityProtocol = SecurityProtocolType.Tls12;

            // Configure Http Request
            WebRequest request = WebRequest.Create(uploadURL);
            request.Method = "POST";
            request.ContentType = "application/x-www-form-urlencoded";
            request.ContentLength = 0;

            // Get Response
            string responseContent = null;
            using (WebResponse response = request.GetResponse())
            {
                using (Stream stream = response.GetResponseStream())
                {
                    using (StreamReader sr99 = new StreamReader(stream))
                    {
                        responseContent = sr99.ReadToEnd();
                    }
                }
            }

            Console.WriteLine(responseContent);

        }
    }
}
\`\`\`
`;

export const getCodeMarkdown = (option: CodeSnippetEnvType, modelData: ModelDataType): string => {
    switch (option) {
        case "python":
            return PYTHON_MD(modelData);
        case "curl":
            return CURL_MD(modelData);
        case "javascript":
            return JAVASCRIPT_MD(modelData);
        case "swift":
            return SWIFT_MD(modelData);
        case "net":
            return NET_MD(modelData);
        default:
            return PYTHON_MD(modelData);
    }
};

export const generateIntegrationCMS = (modelData: ModelDataType): IntegrationCopyCMSType => {
    return {
        [DataMode.IMAGE]: {
            [ImageDeployment.SINGLE]: {
                title: "Single Image (Cloud)",
                icon: "fas fa-cloud",
                description: (
                    <p>
                        You can pass input images as type <code>url</code> or <code>base64</code>:
                    </p>
                ),
                codeSnippets: [
                    {
                        code: getCodeMarkdown(CodeSnippetEnv.CURL, modelData),
                        env: CodeSnippetEnv.CURL
                    },
                    {
                        code: getCodeMarkdown(CodeSnippetEnv.PYTHON, modelData),
                        env: CodeSnippetEnv.PYTHON
                    },
                    {
                        code: getCodeMarkdown(CodeSnippetEnv.JAVASCRIPT, modelData),
                        env: CodeSnippetEnv.JAVASCRIPT
                    }
                ],
                documentationUrl: "https://docs.roboflow.com/deploy/serverless"
            },
            [ImageDeployment.MULTIPLE]: {
                title: "Multiple Images (Cloud)",
                icon: "far fa-cloud",
                description: "Multiple Images description...",
                codeSnippets: [
                    {
                        code: getCodeMarkdown(CodeSnippetEnv.CURL, modelData),
                        env: CodeSnippetEnv.CURL
                    },
                    {
                        code: getCodeMarkdown(CodeSnippetEnv.PYTHON, modelData),
                        env: CodeSnippetEnv.PYTHON
                    },
                    {
                        code: getCodeMarkdown(CodeSnippetEnv.JAVASCRIPT, modelData),
                        env: CodeSnippetEnv.JAVASCRIPT
                    }
                ],
                documentationUrl: "https://docs.roboflow.com/deploy/serverless"
            },
            [ImageDeployment.EDGE]: {
                title: "Edge Detection",
                icon: "fal fa-microchip",
                description: "Edge Detection description...",
                codeSnippets: [
                    {
                        code: getCodeMarkdown(CodeSnippetEnv.CURL, modelData),
                        env: CodeSnippetEnv.CURL
                    },
                    {
                        code: getCodeMarkdown(CodeSnippetEnv.PYTHON, modelData),
                        env: CodeSnippetEnv.PYTHON
                    },
                    {
                        code: getCodeMarkdown(CodeSnippetEnv.JAVASCRIPT, modelData),
                        env: CodeSnippetEnv.JAVASCRIPT
                    }
                ],
                documentationUrl: "https://docs.roboflow.com/deploy/serverless"
            }
        },
        [DataMode.VIDEO]: {
            [VideoDeployment.FILE]: {
                title: "File",
                description: "File description...",
                codeSnippets: [
                    {
                        code: getCodeMarkdown(CodeSnippetEnv.CURL, modelData),
                        env: CodeSnippetEnv.CURL
                    }
                ],
                documentationUrl: "https://docs.roboflow.com/deploy/serverless"
            },
            [VideoDeployment.EDGE]: {
                title: "Edge Detection",
                description: "Edge Detection description...",
                codeSnippets: [
                    {
                        code: getCodeMarkdown(CodeSnippetEnv.CURL, modelData),
                        env: CodeSnippetEnv.CURL
                    }
                ],
                documentationUrl: "https://docs.roboflow.com/deploy/serverless"
            },
            [VideoDeployment.STREAM]: {
                title: "Stream",
                description: "Stream description...",
                codeSnippets: [
                    {
                        code: getCodeMarkdown(CodeSnippetEnv.CURL, modelData),
                        env: CodeSnippetEnv.CURL
                    }
                ],
                documentationUrl: "https://docs.roboflow.com/deploy/serverless"
            }
        }
    };
};
